import React, { useState, useEffect, useRef } from 'react';
import { X, Lightbulb, ChevronRight, Camera, House, Utensils, Mountain, Drama, CircleDollarSign, Map } from 'lucide-react';
import { API_BASE_URL } from '../config';

// Tags with display names and icons
const TAG_CONFIG: Record<string, { label: string; icon: React.ReactNode }> = {
  attractions: { label: 'Attractions', icon: <Camera className="w-4 h-4" /> },
  hotels: { label: 'Hotels', icon: <House className="w-4 h-4" /> },
  food: { label: 'Food', icon: <Utensils className="w-4 h-4" /> },
  experiences: { label: 'Experiences', icon: <Mountain className="w-4 h-4" /> },
  culture: { label: 'Culture', icon: <Drama className="w-4 h-4" /> },
  costs: { label: 'Costs', icon: <CircleDollarSign className="w-4 h-4" /> },
  tips: { label: 'Tips', icon: <Lightbulb className="w-4 h-4" /> },
  itinerary: { label: 'Itinerary', icon: <Map className="w-4 h-4" /> }
};

// Fallback for old TAG_DISPLAY_NAMES usage (if any)
const TAG_DISPLAY_NAMES: Record<string, string> = {
  attractions: 'Attractions',
  hotels: 'Hotels',
  food: 'Food',
  culture: 'Culture',
  costs: 'Costs',
  experiences: 'Experiences',
  tips: 'Tips',
  itinerary: 'Itinerary'
};

interface CuratedQuestion {
  id: string;
  question: string;
  response: string;
  tags: string[];
  suggestionPosition: number;
  placeIds: string[];
  usageCount: number;
  contentType: string;
}

interface QuickTipsData {
  tags: string[];
  questions: CuratedQuestion[];
}

interface QuickTipsOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onQuestionSelect: (question: CuratedQuestion) => void;
  onShowRegistrationPrompt?: (variant: 'google_auth' | 'whatsapp_verify' | 'wait_only', waitMinutes?: number, trigger?: string) => void;
}

/**
 * Generate user-friendly error message based on tier and wait time
 */
function getRateLimitMessage(tier: string, waitMinutes?: number): string {
  const waitText = waitMinutes ? ` Try again in ${waitMinutes} minute${waitMinutes > 1 ? 's' : ''}.` : '';
  
  switch (tier) {
    case 'unregistered':
      return `You've reached your Quick Tips limit (10 per hour). ${waitText}\n\nSign in to unlock 20 per hour. Verify WhatsApp for unlimited (50 per hour).`;
    
    case 'google_auth':
      return `You've reached your Quick Tips limit (20 per hour). ${waitText}\n\nVerify WhatsApp to unlock 50 per hour.`;
    
    case 'whatsapp_verified':
      return `You've reached the global Quick Tips limit for this hour (50 per hour). ${waitText}\n\nThis limit helps us maintain service quality for everyone.`;
    
    default:
      return `Quick Tips limit reached. ${waitText}`;
  }
}

export default function QuickTipsOverlay({
  isOpen,
  onClose,
  onQuestionSelect,
  onShowRegistrationPrompt
}: QuickTipsOverlayProps) {
  const [data, setData] = useState<QuickTipsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // Drag state for drawer
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [drawerPosition, setDrawerPosition] = useState<'half' | 'full'>('half');
  const dragStartY = useRef(0);
  const panelRef = useRef<HTMLDivElement>(null);
  
  // Drawer position heights (as percentage of viewport)
  const HALF_HEIGHT = 50; // 50% of viewport
  const FULL_HEIGHT = 85; // 85% of viewport (leaving some space at top)
  const DRAG_THRESHOLD = 80; // px to trigger snap

  // Fetch data when overlay opens
  useEffect(() => {
    if (isOpen && !data) {
      fetchQuickTips();
    }
  }, [isOpen, data]);

  const fetchQuickTips = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/curated/quick-tips`);
      const result = await response.json();
      
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to load Quick Tips');
      }
    } catch (err) {
      console.error('[QuickTips] Error fetching:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      }
      return [...prev, tag];
    });
  };

  const handleQuestionClick = async (question: CuratedQuestion) => {
    // Check rate limit before proceeding
    try {
      const response = await fetch(`${API_BASE_URL}/api/curated/usage/${question.id}`, { 
        method: 'POST' 
      });
      
      if (response.status === 429) {
        // Rate limit exceeded - use RegistrationPrompt like chat does
        const errorData = await response.json();
        const tier = errorData.tier || 'unregistered';
        const waitMinutes = errorData.waitMinutes;
        
        // Determine registration prompt variant based on tier
        let promptVariant: 'google_auth' | 'whatsapp_verify' | 'wait_only';
        
        if (tier === 'whatsapp_verified') {
          // Verified user - just wait, no action needed
          promptVariant = 'wait_only';
        } else if (tier === 'google_auth') {
          // Registered but not verified - suggest verification
          promptVariant = 'whatsapp_verify';
        } else {
          // Unregistered - suggest sign-up
          promptVariant = 'google_auth';
        }
        
        // Close overlay and show registration prompt with quick_tips_limit trigger
        onClose();
        if (onShowRegistrationPrompt) {
          onShowRegistrationPrompt(promptVariant, waitMinutes, 'quick_tips_limit');
        }
        return;
      }
      
      if (!response.ok) {
        console.error('[Quick Tips] Error recording usage:', response.statusText);
        // Still allow the question to be asked even if we can't record usage
      }
    } catch (e) {
      console.error('[Quick Tips] Error recording usage:', e);
      // Ignore - non-critical, still allow question to be asked
    }
    
    // Close overlay and trigger question in chat
    onQuestionSelect(question);
    onClose();
  };

  // Filter questions by selected tags (show all if none selected)
  const filteredQuestions = data?.questions.filter(q => {
    if (selectedTags.length === 0) return true;
    // Show if question has ANY of the selected tags
    return q.tags.some(tag => selectedTags.includes(tag));
  }) || [];

  // Reset drag state when opening
  useEffect(() => {
    if (isOpen) {
      setDragOffset(0);
      setIsDragging(false);
      setDrawerPosition('half'); // Start at half position
    }
  }, [isOpen]);

  // Calculate current height based on position
  const getDrawerHeight = () => {
    return drawerPosition === 'full' ? FULL_HEIGHT : HALF_HEIGHT;
  };

  // Drag handlers
  const handleDragStart = (clientY: number) => {
    setIsDragging(true);
    dragStartY.current = clientY;
  };

  const handleDragMove = (clientY: number) => {
    if (!isDragging) return;
    
    const delta = clientY - dragStartY.current;
    // Allow dragging both up (negative) and down (positive)
    setDragOffset(delta);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    
    if (drawerPosition === 'half') {
      // From half position
      if (dragOffset < -DRAG_THRESHOLD) {
        // Dragged up - expand to full
        setDrawerPosition('full');
      } else if (dragOffset > DRAG_THRESHOLD) {
        // Dragged down - close
        onClose();
      }
    } else if (drawerPosition === 'full') {
      // From full position
      if (dragOffset > DRAG_THRESHOLD) {
        // Dragged down - collapse to half
        setDrawerPosition('half');
      }
      // Can't drag up further from full
    }
    
    // Reset offset (with animation)
    setDragOffset(0);
  };

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    handleDragStart(e.touches[0].clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    handleDragMove(e.touches[0].clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse event handlers (for desktop testing)
  const handleMouseDown = (e: React.MouseEvent) => {
    handleDragStart(e.clientY);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      handleDragMove(e.clientY);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      handleDragEnd();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 transition-all duration-300 ease-out ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop with blur - MOBILE: full screen, DESKTOP: only left half (chat area) */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm md:w-1/2"
        onClick={onClose}
      />
      
      {/* DESKTOP: Content Panel - slides in from right, covers map area (right 50%) */}
      <div 
        className={`hidden md:flex absolute top-0 right-0 bottom-0 w-1/2 bg-white shadow-2xl flex-col transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Desktop Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#663399] to-[#14b8a6] rounded-xl flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Quick Tips</h2>
                <p className="text-sm text-gray-500">Click a question to get instant answers</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
        </div>
        
        {/* Desktop Tags Filter */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex flex-wrap gap-2">
            {data?.tags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-2 ${
                  selectedTags.includes(tag)
                    ? 'bg-[#663399] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {selectedTags.includes(tag) ? (
                  <span className="text-white">{TAG_CONFIG[tag]?.icon || '•'}</span>
                ) : (
                  <span className="text-gray-700">{TAG_CONFIG[tag]?.icon || '•'}</span>
                )}
                {TAG_CONFIG[tag]?.label || tag}
              </button>
            ))}
          </div>
          {selectedTags.length > 0 && (
            <button 
              onClick={() => setSelectedTags([])}
              className="text-sm text-[#663399] mt-3 underline"
            >
              Clear filters
            </button>
          )}
        </div>
        
        {/* Desktop Questions List */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-10 h-10 border-4 border-[#663399] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="py-12 text-center">
              <p className="text-gray-500 text-lg">{error}</p>
              <button 
                onClick={fetchQuickTips}
                className="mt-4 text-[#663399] underline"
              >
                Retry
              </button>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-gray-500">No questions found for selected tags</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredQuestions.map(question => (
                <button
                  key={question.id}
                  onClick={() => handleQuestionClick(question)}
                  className="w-full text-left p-4 rounded-xl bg-gray-50 hover:bg-gray-100 
                             transition-all duration-200 group hover:shadow-md"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <p className="text-base font-medium text-gray-800 leading-snug">
                        {question.question}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {question.tags.slice(0, 4).map(tag => (
                          <span 
                            key={tag} 
                            className="px-2.5 py-1 bg-gray-200 text-gray-600 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1 
                                             group-hover:text-[#663399] group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* MOBILE: Content Panel - slides up from bottom */}
      <div 
        ref={panelRef}
        className={`md:hidden absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl flex flex-col ${
          isDragging ? '' : 'transition-all duration-300 ease-out'
        }`}
        style={{ 
          height: isOpen 
            ? `calc(${getDrawerHeight()}vh + ${-dragOffset}px)` 
            : '0',
          maxHeight: 'calc(100vh - 60px)',
          minHeight: '200px',
          paddingBottom: 'calc(64px + env(safe-area-inset-bottom))' // Account for bottom nav
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* Draggable Handle Area */}
        <div 
          className="cursor-grab active:cursor-grabbing select-none touch-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          {/* Handle Bar */}
          <div className="flex justify-center pt-3 pb-2">
            <div className={`w-10 h-1 rounded-full transition-colors ${isDragging ? 'bg-gray-400' : 'bg-gray-300'}`} />
          </div>
          
          {/* Header */}
          <div className="px-4 pb-3 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#663399] to-[#14b8a6] rounded-lg flex items-center justify-center">
                <Lightbulb className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Quick Tips</h2>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Tap a question to get instant answers
          </p>
          </div>
        </div>
        
        {/* Tags Filter */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex flex-wrap gap-2">
            {data?.tags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all flex items-center gap-1.5 ${
                  selectedTags.includes(tag)
                    ? 'bg-[#663399] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {selectedTags.includes(tag) ? (
                  <span className="text-white">{TAG_CONFIG[tag]?.icon || '•'}</span>
                ) : (
                  <span className="text-gray-700">{TAG_CONFIG[tag]?.icon || '•'}</span>
                )}
                {TAG_CONFIG[tag]?.label || tag}
              </button>
            ))}
          </div>
          {selectedTags.length > 0 && (
            <button 
              onClick={() => setSelectedTags([])}
              className="text-xs text-[#663399] mt-2 underline"
            >
              Clear filters
            </button>
          )}
        </div>
        
        {/* Questions List */}
        <div className="overflow-y-auto flex-1 overscroll-y-contain">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-[#663399] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="px-4 py-8 text-center">
              <p className="text-gray-500">{error}</p>
              <button 
                onClick={fetchQuickTips}
                className="mt-3 text-sm text-[#663399] underline"
              >
                Retry
              </button>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-gray-500">No questions found for selected tags</p>
            </div>
          ) : (
            <div className="px-4 py-3 space-y-2 pb-16">
              {filteredQuestions.map(question => (
                <button
                  key={question.id}
                  onClick={() => handleQuestionClick(question)}
                  className="w-full text-left p-3 rounded-xl bg-gray-50 hover:bg-gray-100 
                             transition-all duration-200 group active:scale-[0.98]"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800 leading-snug">
                        {question.question}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5 
                                             group-hover:text-[#663399] group-hover:translate-x-0.5 transition-all" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

